import React, { useState, useEffect, useCallback } from "react";
import { FaPlus, FaSpinner, FaTimes, FaChevronDown, FaChevronUp, FaEdit } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { PiPlusBold } from "react-icons/pi";
import { formatNumberWithCommas } from "../../../utils/commonUtils";
import {
  fetchLaundryOrderDetail,
  updateLaundryRequest,
} from "../../../redux/slices/laundrySlice";
import { toast } from "react-toastify";

export default function ModaLaundryItemRequest({ order_id, laundryItems, existingOrderDetails = [] }) {
  const [showModal, setShowModal] = useState(false);
  const status = useSelector((state) => state.laundry?.status);
  const dispatch = useDispatch();

  // Track the expanded item in the accordion
  const [expandedItem, setExpandedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);

  // Check if there are existing order details to determine if we're in edit mode
  useEffect(() => {
    setIsEditMode(existingOrderDetails && existingOrderDetails.length > 0);
  }, [existingOrderDetails]);

  // Move initialFormData inside useCallback to make it stable
  const getInitialFormData = useCallback(() => ({
    order_id,
    order_details: existingOrderDetails && existingOrderDetails.length > 0 ? [...existingOrderDetails] : [],
    price: 0,
  }), [order_id, existingOrderDetails]);

  const [formData, setFormData] = useState(getInitialFormData());

  // Calculate item price - moved outside to avoid dependency issues
  const calculateItemPrice = useCallback((item, detail) => {
    if (!item) return 0;

    let itemTotalPrice = parseFloat(item.price) || 0;

    if (detail.services && Array.isArray(detail.services)) {
      detail.services.forEach((service) => {
        const servicePrice = parseFloat(item[service]);
        if (servicePrice && servicePrice !== 0) {
          itemTotalPrice += servicePrice;
        }
      });
    }

    return itemTotalPrice * (detail.quantity || 1);
  }, []);

  // Calculate total price - wrapped in useCallback to avoid infinite re-renders
  const calculateTotalPrice = useCallback((data) => {
    let totalPrice = 0;

    if (!data.order_details || !Array.isArray(data.order_details) || !Array.isArray(laundryItems)) {
      return totalPrice;
    }

    data.order_details.forEach((detail) => {
      const item = laundryItems.find((i) => i?.name === detail.item);
      totalPrice += calculateItemPrice(item, detail);
    });

    return totalPrice;
  }, [laundryItems, calculateItemPrice]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!showModal) {
      setFormData(getInitialFormData());
      setExpandedItem(null);
      setSearchTerm("");
    }
  }, [showModal, getInitialFormData]);

  // Calculate initial price when component mounts or existingOrderDetails changes
  useEffect(() => {
    if (existingOrderDetails && existingOrderDetails.length > 0) {
      const totalPrice = calculateTotalPrice({
        order_details: existingOrderDetails
      });
      setFormData(prev => ({ ...prev, price: totalPrice }));
    }
  }, [existingOrderDetails, calculateTotalPrice]); // Added calculateTotalPrice to dependencies

  // Filter laundry items based on search - with safe array check
  const filteredLaundryItems = Array.isArray(laundryItems)
    ? laundryItems
        .filter(item => 
          item?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a?.name?.localeCompare(b?.name))
    : [];

  const handleItemCheckboxChange = (itemId, itemName) => {
    const itemExists = formData.order_details.find(
      (detail) => detail.item === itemName
    );

    let updatedOrderDetails;

    if (itemExists) {
      // If the item exists, remove it from the order_details
      updatedOrderDetails = formData.order_details.filter(
        (detail) => detail.item !== itemName
      );
      // Also collapse the accordion if removing the item
      setExpandedItem(null);
    } else {
      // Add a new entry for the item
      updatedOrderDetails = [
        ...formData.order_details,
        { 
          item: itemName, 
          quantity: 1, 
          services: [],
          item_id: itemId // Include item_id for better tracking
        },
      ];
      // Expand the newly added item
      setExpandedItem(itemId);
    }

    const updatedFormData = { ...formData, order_details: updatedOrderDetails };
    setFormData(updatedFormData);

    // Recalculate total price
    const totalPrice = calculateTotalPrice(updatedFormData);
    setFormData((prev) => ({ ...prev, price: totalPrice }));
  };

  const handleServiceCheckboxChange = (itemName, serviceName, servicePrice) => {
    const updatedOrderDetails = formData.order_details.map((detail) => {
      if (detail.item === itemName) {
        const services = detail.services.includes(serviceName)
          ? detail.services.filter((service) => service !== serviceName)
          : [...detail.services, serviceName];
        return { ...detail, services };
      }
      return detail;
    });

    const updatedFormData = { ...formData, order_details: updatedOrderDetails };
    setFormData(updatedFormData);

    // Recalculate total price
    const totalPrice = calculateTotalPrice(updatedFormData);
    setFormData((prev) => ({ ...prev, price: totalPrice }));
  };

  const handleQuantityChange = (itemName, quantity) => {
    const newQuantity = Math.max(1, parseInt(quantity, 10) || 1); // Ensure minimum quantity of 1
    
    const updatedOrderDetails = formData.order_details.map((detail) => {
      if (detail.item === itemName) {
        return { ...detail, quantity: newQuantity };
      }
      return detail;
    });

    const updatedFormData = { ...formData, order_details: updatedOrderDetails };
    setFormData(updatedFormData);

    // Recalculate total price
    const totalPrice = calculateTotalPrice(updatedFormData);
    setFormData((prev) => ({ ...prev, price: totalPrice }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.order_details.length === 0) {
      toast.error("Please select at least one laundry item");
      return;
    }

    // Validate that each selected item has at least one service
    const itemsWithoutServices = formData.order_details.filter(
      detail => !detail.services || detail.services.length === 0
    );

    if (itemsWithoutServices.length > 0) {
      toast.error("Please select at least one service for each item");
      return;
    }

    try {
      const res = await dispatch(updateLaundryRequest(formData));
      
      if (updateLaundryRequest.fulfilled.match(res)) {
        toast.success(res.payload.message);
        dispatch(fetchLaundryOrderDetail(order_id));
        setShowModal(false);
      } else {
        toast.error(res.payload?.message || "Failed to update laundry items");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while updating laundry items");
    }
  };

  const toggleAccordion = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  const getServiceDisplayName = (service) => {
    const serviceNames = {
      wash: "Wash",
      dryclean: "Dry Clean",
      iron: "Iron",
      starch: "Starch"
    };
    return serviceNames[service] || service;
  };

  // Pre-expand items that are already in the order details when modal opens
  useEffect(() => {
    if (showModal && formData.order_details.length > 0 && Array.isArray(laundryItems)) {
      // Expand the first item by default for better UX
      const firstItem = laundryItems.find(item => 
        formData.order_details.some(detail => detail.item === item?.name)
      );
      if (firstItem) {
        setExpandedItem(firstItem.id);
      }
    }
  }, [showModal, formData.order_details, laundryItems]);

  // Safe function to find item details
  const getItemDetail = (itemName) => {
    return formData.order_details.find((detail) => detail.item === itemName);
  };

  // Safe function to check if item is selected
  const isItemSelected = (itemName) => {
    return formData.order_details.some((detail) => detail.item === itemName);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`flex gap-2 ${
          isEditMode 
            ? "bg-[#10B981] hover:bg-[#0DA271]" 
            : "bg-[#1F7BF4] hover:bg-[#1A6BD8]"
        } text-white items-center py-2 px-4 text-[14px] hover:shadow-lg rounded-lg font-medium transition-all duration-200`}
      >
        {isEditMode ? (
          <>
            <FaEdit className="text-lg" />
            Edit Laundry Items
          </>
        ) : (
          <>
            <PiPlusBold className="text-lg font-extrabold" />
            Add Laundry Item Request
          </>
        )}
      </button>

      {showModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="border-0 rounded-lg p-8 shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="absolute top-5 right-4 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center transition-colors duration-200"
                >
                  <FaTimes className="text-gray-300 text-xl" />
                  <span className="sr-only">Close modal</span>
                </button>

                <h1 className="text-2xl text-gray-800 text-center font-medium mb-4">
                  {isEditMode ? "Edit Laundry Items" : "Laundry Item Request"}
                </h1>

                {/* Form */}
                <div className="flex flex-col px-8 py-4 gap-4">
                  {/* Search Bar */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-[#4D5464] font-medium">
                      Search Items:
                    </label>
                    <input
                      type="text"
                      placeholder="Search laundry items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="p-2 border-2 border-[#E0E2E7] rounded-lg text-[14px] focus:outline-none focus:border-[#1F7BF4] transition-colors duration-200"
                    />
                  </div>

                  {/* Selected Items Summary */}
                  {formData.order_details.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">
                        {isEditMode ? "Current Items" : "Selected Items"} ({formData.order_details.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {formData.order_details.map((detail, index) => (
                          <span
                            key={index}
                            className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"
                          >
                            {detail.item} (x{detail.quantity})
                            {detail.services && detail.services.length > 0 && (
                              <span className="text-[10px] opacity-75">
                                - {detail.services.length} service{detail.services.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Total Price Display */}
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-[#4D5464]">
                      Total Price:
                    </label>
                    <div className="pl-3 text-[#333843] p-2 font-bold rounded-lg text-[16px] bg-[#F9F9FC] w-full border-2 border-[#E0E2E7]">
                      <span className="mr-1">&#8358;</span>{" "}
                      {formatNumberWithCommas(
                        formData.price ? formData.price : 0
                      )}
                    </div>
                  </div>

                  {/* Laundry Items Accordion */}
                  <div className="flex flex-col gap-1">
                    <h3 className="text-[14px] text-[#4D5464] font-medium">
                      {isEditMode ? "Update Laundry Items:" : "Select Laundry Items:"}
                    </h3>
                    
                    <div className="grid grid-cols-1 gap-3">
                      {filteredLaundryItems.length > 0 ? (
                        filteredLaundryItems.map((item) => {
                          const isSelected = isItemSelected(item?.name);
                          const detail = getItemDetail(item?.name);
                          
                          return (
                            <div
                              key={item?.id}
                              className="border rounded-lg p-3 transition-all duration-200 hover:shadow-md"
                            >
                              <div className="flex items-center justify-between">
                                <label className="flex items-center gap-3 cursor-pointer flex-1">
                                  <input
                                    type="checkbox"
                                    value={item?.id}
                                    onChange={() =>
                                      handleItemCheckboxChange(item?.id, item?.name)
                                    }
                                    checked={isSelected}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                  <span className="font-medium">{item?.name}</span>
                                  {isSelected && detail && (
                                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                      {detail.quantity} item{detail.quantity > 1 ? 's' : ''}
                                    </span>
                                  )}
                                </label>
                                
                                {isSelected && (
                                  <button
                                    type="button"
                                    onClick={() => toggleAccordion(item?.id)}
                                    className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                  >
                                    {expandedItem === item?.id ? (
                                      <FaChevronUp />
                                    ) : (
                                      <FaChevronDown />
                                    )}
                                  </button>
                                )}
                              </div>

                              {/* Accordion content for quantity and services */}
                              {isSelected && expandedItem === item?.id && (
                                <div className="mt-3 pl-7 border-t pt-3 transition-all duration-300">
                                  {/* Quantity Input */}
                                  <div className="flex gap-3 items-center mb-3">
                                    <label className="text-sm font-medium text-gray-700">
                                      Quantity:
                                    </label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={detail?.quantity || 1}
                                      onChange={(e) =>
                                        handleQuantityChange(
                                          item?.name,
                                          e.target.value
                                        )
                                      }
                                      className="p-2 w-20 border rounded text-sm text-center focus:outline-none focus:border-blue-500 transition-colors duration-200"
                                    />
                                  </div>

                                  {/* Services Selection */}
                                  <div className="mb-3">
                                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                                      Services:
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                      {["wash", "dryclean", "iron", "starch"].map(
                                        (service) => {
                                          const servicePrice = parseFloat(item?.[service]);
                                          const isServiceAvailable = servicePrice !== 0 && !isNaN(servicePrice);
                                          
                                          return (
                                            <label
                                              key={service}
                                              className={`flex items-center gap-2 p-2 rounded border transition-colors duration-200 ${
                                                isServiceAvailable
                                                  ? "cursor-pointer hover:bg-gray-50"
                                                  : "opacity-50 cursor-not-allowed"
                                              } ${
                                                detail?.services?.includes(service)
                                                  ? "bg-blue-50 border-blue-200"
                                                  : "bg-white border-gray-200"
                                              }`}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={detail?.services?.includes(service) || false}
                                                onChange={() =>
                                                  handleServiceCheckboxChange(
                                                    item?.name,
                                                    service,
                                                    servicePrice
                                                  )
                                                }
                                                disabled={!isServiceAvailable}
                                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                              />
                                              <span className="text-sm">
                                                {getServiceDisplayName(service)}
                                                {isServiceAvailable && (
                                                  <span className="text-green-600 ml-1">
                                                    (₦{formatNumberWithCommas(servicePrice)})
                                                  </span>
                                                )}
                                              </span>
                                            </label>
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>

                                  {/* Item Total Price */}
                                  {detail && (
                                    <div className="bg-gray-50 rounded p-2">
                                      <p className="text-sm text-gray-700">
                                        Item Total: <span className="font-semibold">₦{formatNumberWithCommas(calculateItemPrice(item, detail))}</span>
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          {Array.isArray(laundryItems) && laundryItems.length === 0 
                            ? "No laundry items available" 
                            : `No laundry items found${searchTerm ? ` for "${searchTerm}"` : ''}`
                          }
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 justify-center pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-sm text-gray-700 font-semibold bg-gray-100 py-2 px-6 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="flex gap-2 rounded-lg items-center bg-blue-600 text-white py-2 px-6 font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
                      disabled={status === "loading" || formData.order_details.length === 0}
                      onClick={handleSubmit}
                    >
                      {status === "loading" ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          <span>{isEditMode ? 'Updating...' : 'Adding...'}</span>
                        </>
                      ) : (
                        <>
                          <FaPlus className="w-4 h-4" />
                          <span>{isEditMode ? 'Update Laundry Items' : 'Add Laundry Items'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </>
  );
}